import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import DramaCard from "@/components/feature/DramaCard";
import FilterBar, { type CategoryFilters } from "./components/FilterBar";
import Pagination from "./components/Pagination";

const PAGE_SIZE = 6;

const defaultFilters: CategoryFilters = { type: "", status: "", year: "", sort: "update" };

export default function Category() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const typeFromUrl = searchParams.get("type")?.trim() ?? "";
  const statusFromUrl = searchParams.get("status")?.trim() ?? "";

  const [filters, setFilters] = useState<CategoryFilters>({
    ...defaultFilters,
    type: typeFromUrl,
    status: statusFromUrl,
  });
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setFilters({ ...defaultFilters, type: typeFromUrl, status: statusFromUrl });
    setPage(1);
  }, [typeFromUrl, statusFromUrl, keyword]);

  useEffect(() => {
    document.title = `${filters.type || "电视剧分类"} - 迷人的保姆`;
  }, [filters.type]);

  const filtered = useMemo(() => {
    let list = [...dramas];
    if (keyword) {
      list = list.filter(
        (drama) =>
          drama.title.includes(keyword) ||
          drama.subtitle.includes(keyword) ||
          drama.cast.some((name) => name.includes(keyword)) ||
          drama.roles.some((role) => role.name.includes(keyword) || role.actor.includes(keyword))
      );
    }
    if (filters.type) list = list.filter((drama) => drama.categories.includes(filters.type));
    if (filters.status) list = list.filter((drama) => drama.status === filters.status);
    if (filters.year) list = list.filter((drama) => String(drama.year) === filters.year);

    list.sort((a, b) => {
      if (filters.sort === "rating") return b.rating - a.rating;
      if (filters.sort === "year") return b.year - a.year;
      return a.updateTime < b.updateTime ? 1 : -1;
    });
    return list;
  }, [filters, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function patchFilters(patch: Partial<CategoryFilters>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }

  function resetFilters() {
    setFilters({ ...defaultFilters });
    setPage(1);
  }

  const activeType = filters.type ? `${filters.type}` : "全部剧集";

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 lg:px-8">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">电视剧分类</span>
        {filters.type && (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{filters.type}</span>
          </>
        )}
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {activeType} - 迷人的保姆
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {keyword
            ? `正在展示与“${keyword}”相关的剧集，共找到 ${filtered.length} 部。`
            : "按剧情类型、更新状态与首播年份筛选你感兴趣的剧集，快速找到下一部值得追的好剧。"}
        </p>
      </header>

      <div className="mt-5 hidden rounded-lg border border-background-200 bg-background-50 p-4 md:block md:p-5">
        <FilterBar filters={filters} onChange={patchFilters} onReset={resetFilters} />
      </div>

      <div className="mt-4 flex items-center justify-between md:hidden">
        <span className="text-xs text-foreground-500">共 {filtered.length} 部剧集</span>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-medium text-background-50"
        >
          <i className="ri-filter-3-line"></i>
          筛选
        </button>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {pageItems.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-archive-line text-2xl"></i>
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">暂无符合条件的剧集</p>
          <p className="mt-1 text-xs text-foreground-500">试试调整筛选条件，或重置后重新浏览</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-4 flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setPanelOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/50"
          ></button>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setPanelOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <FilterBar filters={filters} onChange={patchFilters} onReset={resetFilters} />
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 部剧集
            </button>
          </div>
        </div>
      )}
    </div>
  );
}